// StrategicAssessment.tsx
// Professional Enterprise Maturity Assessment Component
// Generates consultancy-grade PDF reports

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import styles from './StrategicAssessment.module.css';
import {
  Download, ArrowRight, Loader, RotateCcw, Calendar,
  Shield, Layers, Brain, BarChart3, Target, Clock
} from 'lucide-react';

// Dimension icons and colors 
const dimensionConfig: Record<string, { icon: React.ReactNode; color: string }> = {
  regulatory: { icon: <Shield size={16} />, color: '#ef4444' },     // Red
  integration: { icon: <Layers size={16} />, color: '#3b82f6' },    // Blue  
  ai: { icon: <Brain size={16} />, color: '#8b5cf6' },              // Purple
  transparency: { icon: <BarChart3 size={16} />, color: '#10b981' }, // Green
  strategy: { icon: <Target size={16} />, color: '#f59e0b' },        // Amber
};

type Step = 'intro' | 'question' | 'analyzing' | 'result';

// Peer benchmark (weighted average from DACH data)
const PEER_SCORE = 124; // Out of 200

export const StrategicAssessment: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const diagnostic = t.diagnostic;

  const [step, setStep] = useState<Step>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [score, setScore] = useState(0);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  // Current question
  const currentQuestion = diagnostic.questions[currentQuestionIndex];
  const totalQuestions = diagnostic.questions.length;
  const progressPercentage = ((currentQuestionIndex) / totalQuestions) * 100;

  // Analysis animation
  useEffect(() => {
    if (step === 'analyzing') {
      const timers = [
        setTimeout(() => setAnalysisStep(1), 600),
        setTimeout(() => setAnalysisStep(2), 1200),
        setTimeout(() => setAnalysisStep(3), 1800),
        setTimeout(() => {
          const totalScore = Object.values(answers).reduce((acc: number, val: number) => acc + val, 0);
          setScore(totalScore);
          setStep('result');
        }, 2600),
      ];
      return () => timers.forEach(clearTimeout);
    }
  }, [step, answers]);

  const handleStart = useCallback(() => {
    setStep('question');
    setCurrentQuestionIndex(0);
    setAnswers({});
    setSelectedOption(null);
    setAnalysisStep(0);
  }, []);

  const handleAnswer = useCallback((value: number) => {
    if (!currentQuestion) return;

    setSelectedOption(value);

    // Brief visual feedback before transitioning
    setTimeout(() => {
      setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
      setSelectedOption(null);

      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        setStep('analyzing');
      }
    }, 200);
  }, [currentQuestion, currentQuestionIndex, totalQuestions]);

  const handleBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      const prevQuestion = diagnostic.questions[currentQuestionIndex - 1];
      // Remove the previous answer when going back
      setAnswers(prev => {
        const updated = { ...prev };
        delete updated[prevQuestion.id];
        return updated;
      });
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      setStep('intro');
    }
  }, [currentQuestionIndex, diagnostic.questions]);

  const handleRestart = useCallback(() => {
    setStep('intro');
    setCurrentQuestionIndex(0);
    setAnswers({});
    setScore(0);
    setSelectedOption(null);
    setAnalysisStep(0);
  }, []);

  const handleDownload = async () => {
    setIsGeneratingPdf(true);
    try {
      const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
      const { getAssessmentContent } = await import('../../utils/assessmentReportContent');

      const content = getAssessmentContent(score, answers, language as 'de' | 'en');

      const pdfDoc = await PDFDocument.create();
      let page = pdfDoc.addPage([595, 842]); // A4 size
      const { width, height } = page.getSize();

      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Colors
      const colorPrimary = rgb(0.06, 0.09, 0.16);
      const colorText = rgb(0.2, 0.2, 0.2);
      const colorLightGray = rgb(0.96, 0.96, 0.98);
      const colorMuted = rgb(0.5, 0.5, 0.5);

      let y = height - 50;

      // --- Header ---
      page.drawText('INTEGROVA AI', { x: 50, y, size: 18, font: fontBold, color: colorPrimary });
      y -= 15;
      page.drawText('Enterprise Integration & AI Governance', { x: 50, y, size: 10, font: fontRegular, color: colorMuted });

      y -= 40;
      page.drawText(content.title, { x: 50, y, size: 24, font: fontBold, color: colorText });
      y -= 20;
      page.drawText(`${content.dateLabel} ${new Date().toLocaleDateString()}`, { x: 50, y, size: 10, font: fontRegular, color: rgb(0.4, 0.4, 0.4) });

      y -= 35;

      // --- Summary Section ---
      page.drawRectangle({ x: 40, y: y - 80, width: width - 80, height: 90, color: colorLightGray });

      const summaryY = y - 20;
      page.drawText(content.summaryTitle, { x: 50, y: summaryY, size: 14, font: fontBold, color: colorPrimary });

      // Word wrap for summary text
      const summaryWords = content.summaryText.split(' ');
      let line = '';
      let lineY = summaryY - 22;
      const maxWidth = width - 100;

      for (const word of summaryWords) {
        const testLine = line + (line ? ' ' : '') + word;
        const testWidth = fontRegular.widthOfTextAtSize(testLine, 10);
        if (testWidth > maxWidth && line) {
          page.drawText(line, { x: 50, y: lineY, size: 10, font: fontRegular, color: colorText });
          lineY -= 14;
          line = word;
        } else {
          line = testLine;
        }
      }
      if (line) {
        page.drawText(line, { x: 50, y: lineY, size: 10, font: fontRegular, color: colorText });
      }

      y -= 120;

      // --- Score Visualization ---
      page.drawText(`${content.scoreLabel}: ${score}/200`, { x: 50, y, size: 14, font: fontBold, color: colorPrimary });
      page.drawText(`${content.peerLabel}: ${PEER_SCORE}/200`, { x: 300, y, size: 14, font: fontBold, color: colorMuted });

      y -= 18;
      // User Score Bar
      page.drawRectangle({ x: 50, y, width: 200, height: 10, color: rgb(0.9, 0.9, 0.9) });
      page.drawRectangle({ x: 50, y, width: (score / 200) * 200, height: 10, color: colorPrimary });

      // Peer Score Bar
      page.drawRectangle({ x: 300, y, width: 200, height: 10, color: rgb(0.9, 0.9, 0.9) });
      page.drawRectangle({ x: 300, y, width: (PEER_SCORE / 200) * 200, height: 10, color: colorMuted });

      y -= 40;
      page.drawLine({ start: { x: 50, y }, end: { x: width - 50, y }, thickness: 1, color: rgb(0.85, 0.85, 0.85) });
      y -= 30;

      // --- Dimensions Breakdown ---
      page.drawText(content.dimensionTitle, { x: 50, y, size: 16, font: fontBold, color: colorPrimary });
      y -= 30;

      for (const dim of content.dimensions) {
        if (y < 120) {
          page = pdfDoc.addPage([595, 842]);
          y = height - 50;
        }

        // Dimension Label
        page.drawText(dim.label, { x: 50, y, size: 12, font: fontBold, color: colorText });

        // Status with color
        const percentage = dim.max > 0 ? (dim.score / dim.max) * 100 : 0;
        const statusColor = percentage >= 80 ? rgb(0.1, 0.6, 0.3) : percentage >= 50 ? rgb(0.85, 0.55, 0.1) : rgb(0.8, 0.15, 0.15);

        page.drawText(dim.status, { x: 350, y, size: 10, font: fontBold, color: statusColor });

        // Score bar
        y -= 15;
        page.drawRectangle({ x: 50, y, width: 200, height: 6, color: rgb(0.9, 0.9, 0.9) });
        page.drawRectangle({ x: 50, y, width: (percentage / 100) * 200, height: 6, color: statusColor });

        page.drawText(`${dim.score}/${dim.max}`, { x: 260, y: y - 2, size: 9, font: fontRegular, color: colorMuted });

        y -= 18;

        // Insight text with word wrap
        const insightWords = dim.text.split(' ');
        let insightLine = '';
        const insightMaxWidth = width - 100;

        for (const word of insightWords) {
          const testLine = insightLine + (insightLine ? ' ' : '') + word;
          const testWidth = fontRegular.widthOfTextAtSize(testLine, 9);
          if (testWidth > insightMaxWidth && insightLine) {
            page.drawText(insightLine, { x: 50, y, size: 9, font: fontRegular, color: rgb(0.35, 0.35, 0.35) });
            y -= 12;
            insightLine = word;
          } else {
            insightLine = testLine;
          }
        }
        if (insightLine) {
          page.drawText(insightLine, { x: 50, y, size: 9, font: fontRegular, color: rgb(0.35, 0.35, 0.35) });
          y -= 12;
        }

        y -= 20;
      }

      // --- Footer ---
      const footerY = 35;
      page.drawLine({ start: { x: 50, y: footerY + 10 }, end: { x: width - 50, y: footerY + 10 }, thickness: 0.5, color: rgb(0.85, 0.85, 0.85) });
      page.drawText(content.footerText, { x: 50, y: footerY - 5, size: 8, font: fontRegular, color: colorMuted });
      page.drawText('Confidential', { x: width - 100, y: footerY - 5, size: 8, font: fontRegular, color: colorMuted });

      // Generate and download using FileSaver-style approach
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
      const fileName = `Integrova_Maturity_Assessment_${new Date().toISOString().split('T')[0]}.pdf`;

      // FileSaver.js approach - more reliable for filename handling
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;

      // Trigger download via MouseEvent for better browser compatibility
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: false
      });
      link.dispatchEvent(clickEvent);

      // Clean up after a delay
      setTimeout(() => URL.revokeObjectURL(url), 10000);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert(language === 'de'
        ? 'PDF-Generierung fehlgeschlagen. Bitte erneut versuchen.'
        : 'PDF generation failed. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Calculate score percentage for result display
  const scorePercentage = Math.round((score / 200) * 100);
  const peerPercentage = Math.round((PEER_SCORE / 200) * 100);

  return (
    <section className={styles.diagnostic} id="assessment">
      <div className={styles.diagnostic__container}>

        {/* ===== INTRO STEP ===== */}
        {step === 'intro' && (
          <>
            <div className={styles.diagnostic__content}>
              <span className={styles.diagnostic__tag}>{diagnostic.tag}</span>
              <h2 className={styles.diagnostic__title}>
                {diagnostic.title.split(' ').map((word, i) =>
                  i === 0 ? <span key={i} className={styles.diagnostic__titleHighlight}>{word} </span> : word + ' '
                )}
              </h2>
              <p className={styles.diagnostic__subtitle}>{diagnostic.subtitle}</p>
              <button onClick={handleStart} className={styles.diagnostic__startBtn}>
                <span>{diagnostic.startBtn}</span>
                <ArrowRight size={20} />
              </button>
              <p className={styles.diagnostic__microCopy}>
                {diagnostic.noEmail} • {diagnostic.estimatedTime}
              </p>
            </div>
            <div className={styles.diagnostic__wizardWrapper}>
              <div
                className={styles.diagnostic__previewCard}
                onClick={handleStart}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleStart()}
              >
                {/* Time Badge */}
                <div className={styles.diagnostic__timeBadge}>
                  <Clock size={14} />
                  <span>{diagnostic.estimatedTime}</span>
                </div>
                <div className={styles.diagnostic__previewHeader}>
                  <span>{totalQuestions} {language === 'de' ? 'strategische Fragen' : 'strategic questions'}</span>
                </div>
                <div className={styles.diagnostic__previewDimensions}>
                  {Object.entries(diagnostic.dimensions).map(([key, dim]) => {
                    const config = dimensionConfig[key] || { icon: <Target size={16} />, color: '#64748b' };
                    return (
                      <div
                        key={key}
                        className={styles.diagnostic__previewDim}
                        style={{ '--dim-color': config.color } as React.CSSProperties}
                      >
                        <span className={styles.diagnostic__dimIcon} style={{ color: config.color }}>
                          {config.icon}
                        </span>
                        <span>{dim.name}</span>
                        <ArrowRight size={14} className={styles.diagnostic__dimArrow} />
                      </div>
                    );
                  })}
                </div>
                <div className={styles.diagnostic__previewCta}>
                  <span>{language === 'de' ? 'Assessment starten' : 'Start Assessment'}</span>
                  <ArrowRight size={16} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ===== QUESTION STEP ===== */}
        {step === 'question' && currentQuestion && (
          <>
            <div className={styles.diagnostic__content}>
              <span className={styles.diagnostic__tag}>
                {diagnostic.dimensions[currentQuestion.dimension].name}
              </span>
              <h3 className={styles.diagnostic__questionTitle}>
                {currentQuestion.text}
              </h3>
              {currentQuestion.context && (
                <p className={styles.diagnostic__questionContext}>
                  {currentQuestion.context}
                </p>
              )}
            </div>
            <div className={styles.diagnostic__wizardWrapper}>
              <div className={styles.diagnostic__wizard}>
                <div className={styles.diagnostic__header}>
                  <span className={styles.diagnostic__progressLabel}>
                    {diagnostic.progress} {currentQuestionIndex + 1}/{totalQuestions}
                  </span>
                  <div className={styles.diagnostic__progressBar}>
                    <div style={{ width: `${progressPercentage}%` }} />
                  </div>
                </div>

                <div className={styles.diagnostic__options}>
                  {currentQuestion.options.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(opt.value)}
                      className={`${styles.diagnostic__option} ${selectedOption === opt.value ? styles.diagnostic__optionSelected : ''
                        }`}
                      disabled={selectedOption !== null}
                    >
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>

                {/* Navigation Row */}
                <div className={styles.diagnostic__navRow}>
                  <button
                    onClick={handleBack}
                    className={styles.diagnostic__navBtn}
                    type="button"
                  >
                    <ArrowRight size={16} style={{ transform: 'rotate(180deg)' }} />
                    <span>{language === 'de' ? 'Zurück' : 'Back'}</span>
                  </button>
                  <button
                    onClick={handleRestart}
                    className={`${styles.diagnostic__navBtn} ${styles.diagnostic__navBtnReset}`}
                    type="button"
                  >
                    <RotateCcw size={14} />
                    <span>{language === 'de' ? 'Neu starten' : 'Restart'}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ===== ANALYZING STEP ===== */}
        {step === 'analyzing' && (
          <div className={styles.diagnostic__analyzing}>
            <Loader className={styles.diagnostic__spinner} size={48} />
            <h3 className={styles.diagnostic__analyzingTitle}>{diagnostic.analyzing.title}</h3>
            <div className={styles.diagnostic__log}>
              <p className={analysisStep >= 0 ? styles.activeLog : ''}>{diagnostic.analyzing.step1}</p>
              <p className={analysisStep >= 1 ? styles.activeLog : ''}>{diagnostic.analyzing.step2}</p>
              <p className={analysisStep >= 2 ? styles.activeLog : ''}>{diagnostic.analyzing.step3}</p>
              <p className={analysisStep >= 3 ? styles.activeLog : ''}>{diagnostic.analyzing.step4}</p>
            </div>
          </div>
        )}

        {/* ===== RESULT STEP ===== */}
        {step === 'result' && (
          <>
            <div className={styles.diagnostic__content}>
              <span className={styles.diagnostic__tag}>{diagnostic.tag}</span>
              <h2 className={styles.diagnostic__title}>
                {language === 'de' ? 'Analyse abgeschlossen' : 'Analysis Complete'}
              </h2>
              <p className={styles.diagnostic__subtitle}>{diagnostic.result.note}</p>
            </div>
            <div className={styles.diagnostic__wizardWrapper}>
              <div className={styles.diagnostic__resultCard}>
                <div className={styles.diagnostic__scoreGrid}>
                  <div className={styles.diagnostic__scoreCard}>
                    <span className={styles.diagnostic__scoreLabel}>{diagnostic.result.scoreTitle}</span>
                    <span className={styles.diagnostic__scoreValue}>{score}</span>
                    <span className={styles.diagnostic__scoreMax}>/200 ({scorePercentage}%)</span>
                    <div className={styles.diagnostic__scoreBar}>
                      <div style={{ width: `${scorePercentage}%` }} className={styles.diagnostic__scoreBarFill} />
                    </div>
                  </div>
                  <div className={`${styles.diagnostic__scoreCard} ${styles.diagnostic__scoreCardPeer}`}>
                    <span className={styles.diagnostic__scoreLabel}>{diagnostic.result.peerTitle}</span>
                    <span className={styles.diagnostic__scoreValue}>{PEER_SCORE}</span>
                    <span className={styles.diagnostic__scoreMax}>/200 ({peerPercentage}%)</span>
                    <div className={styles.diagnostic__scoreBar}>
                      <div style={{ width: `${peerPercentage}%` }} className={styles.diagnostic__scoreBarFillPeer} />
                    </div>
                  </div>
                </div>

                <div className={styles.diagnostic__actions}>
                  <button
                    onClick={handleDownload}
                    className={styles.diagnostic__downloadBtn}
                    disabled={isGeneratingPdf}
                  >
                    {isGeneratingPdf ? (
                      <>
                        <Loader className={styles.spin} size={18} />
                        <span>{diagnostic.result.downloading}</span>
                      </>
                    ) : (
                      <>
                        <Download size={18} />
                        <span>{diagnostic.result.downloadBtn}</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => navigate('/consultation')}
                    className={styles.diagnostic__scheduleBtn}
                  >
                    <Calendar size={18} />
                    <span>{diagnostic.result.scheduleBtn}</span>
                  </button>

                  <button onClick={handleRestart} className={styles.diagnostic__restartBtn}>
                    <RotateCcw size={16} />
                    <span>{diagnostic.result.restartBtn}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

      </div>
    </section>
  );
};

export default StrategicAssessment;